import { gql, useMutation } from '@apollo/client';
import IconChevronLeft from '@apollo/icons/default/IconChevronLeft.svg';
import IconChevronRight from '@apollo/icons/default/IconChevronRight.svg';
import IconCode from '@apollo/icons/default/IconCode.svg';
import IconErrorSolid from '@apollo/icons/default/IconErrorSolid.svg';
import IconInfo from '@apollo/icons/default/IconInfo.svg';
import IconRun from '@apollo/icons/default/IconRun.svg';
import IconWarningSolid from '@apollo/icons/default/IconWarningSolid.svg';
import {
  Button,
  ButtonGroup,
  CloseButton,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Spinner,
} from '@apollo/orbit';
import classnames from 'classnames';
import { GraphQLSchema } from 'graphql';
import _ from 'lodash';
import React, { Fragment, useCallback } from 'react';

import { EnvironmentVariablesEditor } from 'src/app/graph/explorerPage/environmentVariablesEditor/EnvironmentVariablesEditor';
import { ExplorerBehavioralFlags } from 'src/app/graph/explorerPage/explorerBehavioralFlags';
import { updateAllEnvironmentVariableEditors } from 'src/app/graph/explorerPage/hooks/useExplorerState/useEnvironmentVariables';
import {
  useLocalStorageGraphIdentifier,
  usePerGraphIdentifierLocalStorage,
} from 'src/app/graph/explorerPage/hooks/usePerGraphIdentifierLocalStorage';
import { ScriptModalSnippets } from 'src/app/graph/explorerPage/ScriptModalSnippets';
import { GraphRef } from 'src/app/graph/hooks/useGraphRef';
import { ResizerStyles } from 'src/components/common/resizerStyles/ResizerStyles';
import { ThreePaneLayout } from 'src/components/common/ThreePaneLayout';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import {
  StudioEditorContainer,
  useStudioEditor,
} from 'src/hooks/useStudioEditor';
import {
  SavePreflightScriptForGraphVariant,
  SavePreflightScriptForGraphVariantVariables,
} from 'src/lib/graphqlTypes/types';
import { DocsRouter } from 'src/lib/routers';

import { ExplorerDocsTab } from '../helpers/ExplorerDocsTab';
import { useScriptModalState } from '../useScriptModalState';

import { PreflightScriptOAuthConfirmationModal } from './PreflightScriptOAuthConfirmationModal';
import { ScriptPromptModal } from './ScriptPromptModal';

export const PreflightScriptEditModal = ({
  currentPreflightScript = '',
  onClose,
  graphRef,
  endpointUrl,
  includeCookies,
  explorerBehavioralFlags,
  isOperationScriptEnabled,
  setDocsTab,
  graphqlSchema,
}: {
  currentPreflightScript: string | undefined;
  onClose: () => void;
  graphRef: GraphRef | null;
  endpointUrl: string | null;
  includeCookies: boolean;
  explorerBehavioralFlags: ExplorerBehavioralFlags;
  isOperationScriptEnabled: boolean;
  setDocsTab: (newDocsTab: ExplorerDocsTab) => void;
  graphqlSchema: GraphQLSchema;
}) => {
  const localStorageGraphIdentifier = useLocalStorageGraphIdentifier({
    shouldNamespaceSandboxByEndpoint: true,
    shouldOnlyStoreForRegisteredGraphs: false,
  });
  const [, setSandboxPreflightScript] = usePerGraphIdentifierLocalStorage({
    key: 'sandboxPreflightScript',
    graphIdentifier: localStorageGraphIdentifier,
    stableInitialValue: undefined,
  });
  const scriptEditor = useStudioEditor({
    language: 'javascript',
    defaultValue: currentPreflightScript,
  });
  const {
    logRef,
    layoutInfo,
    logs,
    environmentVariablesEditor,
    testRunScript,
    scriptRunning,
    oauth2Request,
    promptRequest,
    model,
  } = useScriptModalState({
    graphRef,
    editor: scriptEditor,
    initialScriptValue: currentPreflightScript,
    onClose,
    endpointUrl,
    includeCookies,
    explorerBehavioralFlags,
    currentOperationId: undefined,
    isOperationScriptEnabled,
    setDocsTab,
    graphqlSchema,
  });

  const handleClose = useCallback(() => {
    updateAllEnvironmentVariableEditors();
    onClose();
  }, [onClose]);

  const [savePreflightScriptToGraphVariant, { loading, error }] = useMutation<
    SavePreflightScriptForGraphVariant,
    SavePreflightScriptForGraphVariantVariables
  >(
    gql`
      mutation SavePreflightScriptForGraphVariant(
        $graphId: ID!
        $graphVariant: String!
        $preflightScriptContent: String
      ) {
        service(id: $graphId) {
          variant(name: $graphVariant) {
            updatePreflightScript(preflightScript: $preflightScriptContent) {
              id
              preflightScript
            }
          }
        }
      }
    `,
    { onCompleted: () => handleClose() },
  );

  return (
    <>
      <Modal
        isOpen={true}
        onClose={handleClose}
        size="6xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent className="h-[80vh] md:mx-20">
          <ModalHeader className="border-b border-b-silver-dark">
            <ModalTitle>Edit your Preflight Script</ModalTitle>
            <ModalDescription>
              This script will run in each user's browser and be stored in plain
              text on our servers. Don't share any secrets here 🤫. All team
              members can view the script and toggle it off when they need to.{' '}
              <Link
                className="whitespace-nowrap"
                isExternal
                to={DocsRouter.path('ExplorerConnectingWithHash', {
                  hash: 'preflight-scripts',
                })}
              >
                Learn more
              </Link>
              .
            </ModalDescription>
          </ModalHeader>
          <ModalBody className="-my-2 p-0">
            <ResizerStyles
              additionalCss={`
        .xxx-script-pane-layout .Resizer {
          margin-left: -6px;
          margin-right: -6px;
          z-index: 1;
        }
        .xxx-script-pane-layout .Resizer:before {
          display: none;
        }
      `}
            />

            <ThreePaneLayout
              layoutInfo={layoutInfo}
              className="xxx-script-pane-layout"
              leftPanel={
                <div className="flex h-full flex-col border-r border-primary">
                  <div className="ml-6 mr-2 mt-4 flex flex-none items-center font-semibold">
                    <div className="flex flex-1 items-center">
                      <IconCode /> Snippets
                    </div>
                    <CloseButton
                      onClick={() => layoutInfo.setLeftPaneOpen(false)}
                      size="sm"
                    />
                  </div>
                  <div className="flex-1 overflow-auto pb-4">
                    <div className="ml-8 mr-6 flex-none py-2 text-sm text-secondary">
                      Use these snippets to set and access environment
                      variables, fetch operations, send network requests, and
                      prompt for user input.
                    </div>
                    <ScriptModalSnippets
                      scriptEditor={scriptEditor}
                      model={model}
                    />
                  </div>
                </div>
              }
              centerPanel={
                <div className="relative flex size-full flex-1 flex-col overflow-hidden">
                  <div
                    className={classnames(
                      'flex h-10 flex-none items-center justify-between border-b border-r border-primary pr-6',
                      layoutInfo.leftPaneOpen ? 'pl-4' : 'pl-9',
                    )}
                  >
                    <div className="text-sm font-semibold">Script editor</div>
                    <Button
                      variant="hidden"
                      onClick={() => testRunScript()}
                      type="button"
                      leftIcon={
                        scriptRunning ? (
                          <Spinner
                            size="tiny"
                            className="w-2 -translate-x-px scale-150"
                            variant="neutral"
                          />
                        ) : (
                          <IconRun />
                        )
                      }
                      size="sm"
                    >
                      Test script
                    </Button>
                  </div>
                  <div className="relative flex flex-1 border-r border-primary bg-primary">
                    <StudioEditorContainer
                      editor={scriptEditor}
                      className={classnames(
                        // Exclude scripts from fullstory
                        'fs-exclude flex-1 overflow-hidden pr-4',
                        layoutInfo.leftPaneOpen ? '' : 'pl-2',
                      )}
                    />
                  </div>
                </div>
              }
              rightPanel={
                <div className="flex size-full flex-1 flex-col">
                  <div className="flex h-10 flex-none items-center border-b border-r border-primary pl-4 pr-9 text-sm font-semibold">
                    Console output
                  </div>
                  <div
                    className="flex-1 overflow-auto bg-silver-lighter py-2 pl-2 pr-9 font-mono text-xs"
                    ref={logRef}
                  >
                    {logs
                      .slice()
                      .reverse()
                      .map((runLogs, runIndex) => {
                        return (
                          // eslint-disable-next-line react/no-array-index-key
                          <Fragment key={runIndex}>
                            {runIndex > 0 && (
                              <div className="my-1 ml-4 border-b border-dashed" />
                            )}
                            {runLogs.map(([level, ...msgs], logIndex) => {
                              return (
                                <span
                                  // eslint-disable-next-line react/no-array-index-key
                                  key={`${runIndex}${logIndex}`}
                                  className={classnames({
                                    'text-blilet-dark': level === 'log',
                                    'text-yellow-dark': level === 'warn',
                                    'text-red-dark': level === 'error',
                                  })}
                                >
                                  {level === 'error' ? (
                                    <IconErrorSolid />
                                  ) : level === 'warn' ? (
                                    <IconWarningSolid />
                                  ) : (
                                    <div className="inline-block size-2" />
                                  )}{' '}
                                  {_.capitalize(level)}: {msgs.join(' ')}
                                  <br />
                                </span>
                              );
                            })}
                          </Fragment>
                        );
                      })}
                  </div>
                  <div className="flex h-10 flex-none items-center border-y border-primary pl-4 pr-9 text-sm font-semibold">
                    Environment Variables
                  </div>
                  <EnvironmentVariablesEditor
                    editor={environmentVariablesEditor}
                    className="flex-1  bg-silver-lighter"
                  />
                </div>
              }
            />

            {error && <ErrorMessage error={error} />}
          </ModalBody>
          <ModalFooter className="border-t border-t-primary">
            <Button
              variant="hidden"
              onClick={() => layoutInfo.setLeftPaneOpen((v) => !v)}
              leftIcon={
                layoutInfo.leftPaneOpen ? (
                  <IconChevronLeft />
                ) : (
                  <IconChevronRight />
                )
              }
            >
              {layoutInfo.leftPaneOpen ? 'Hide' : 'Show'} snippets
            </Button>
            <div className="mx-6 flex items-center text-sm text-secondary">
              <IconInfo className="mr-2" />
              <div>
                Changes made to this Preflight Script will apply to all users on
                your team using this variant.
              </div>
            </div>
            <ButtonGroup>
              <Button variant="secondary" onClick={handleClose} type="button">
                Close
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  if (graphRef) {
                    savePreflightScriptToGraphVariant({
                      variables: {
                        ...graphRef,
                        preflightScriptContent: scriptEditor.getValue(),
                      },
                    });
                  } else {
                    setSandboxPreflightScript(scriptEditor.getValue());
                    handleClose();
                  }
                }}
                type="button"
                isLoading={loading}
                loadingText="Save"
              >
                Save
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {oauth2Request && (
        <PreflightScriptOAuthConfirmationModal
          {...oauth2Request}
          isOpen={!!oauth2Request}
        />
      )}
      {promptRequest && <ScriptPromptModal {...promptRequest} />}
    </>
  );
};
